/**
 * Platform-Aware Storage Backend
 *
 * Provides a unified synchronous API over:
 * - iOS native: @capacitor/preferences (UserDefaults — survives Safari eviction)
 * - Web: localStorage (standard browser storage)
 *
 * Design: In-memory Map cache with async write-through to native.
 * Reads are always synchronous from the cache. Writes update the cache
 * immediately and persist asynchronously to the native layer.
 */

import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

// In-memory cache for synchronous reads
const cache = new Map<string, string>();
let initialized = false;
let native = false;

// Migration flag
const MIGRATION_KEY = '_madina_migration_v1_done';

/**
 * Whether we're running on a native platform (iOS).
 */
export function isNative(): boolean {
  return native;
}

/**
 * Whether the storage backend has been initialized.
 */
export function isInitialized(): boolean {
  return initialized;
}

/**
 * Initialize the storage backend. Must be called before React mounts.
 * On native: loads all Preferences into the in-memory cache.
 * On web: no-op (localStorage is always synchronous).
 */
export async function initStorageBackend(): Promise<void> {
  if (initialized) return;

  try {
    native = Capacitor.isNativePlatform();
  } catch {
    native = false;
  }

  if (native) {
    // Load all Preferences into cache
    const { keys } = await Preferences.keys();
    for (const key of keys) {
      const { value } = await Preferences.get({ key });
      if (value !== null) {
        cache.set(key, value);
      }
    }

    // One-time migration from localStorage to Preferences
    await migrateFromLocalStorage();
  }

  initialized = true;
}

/**
 * Migrate localStorage data to Preferences on first native launch.
 * Keeps localStorage populated too (dual-write) so the web version still works.
 */
async function migrateFromLocalStorage(): Promise<void> {
  const { value } = await Preferences.get({ key: MIGRATION_KEY });
  if (value === 'true') return; // Already migrated

  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    // No localStorage available (SSR or weird env)
    await Preferences.set({ key: MIGRATION_KEY, value: 'true' });
    return;
  }

  let migrated = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;

    // Skip if already in Preferences
    if (cache.has(key)) continue;

    const val = localStorage.getItem(key);
    if (val !== null) {
      await Preferences.set({ key, value: val });
      cache.set(key, val);
      migrated++;
    }
  }

  await Preferences.set({ key: MIGRATION_KEY, value: 'true' });
  cache.set(MIGRATION_KEY, 'true');

  if (migrated > 0) {
    console.info(`[StorageBackend] Migrated ${migrated} keys from localStorage to Preferences`);
  }
}

/**
 * Read a value synchronously from the storage backend.
 * On native: reads from in-memory cache (populated at init).
 * On web: reads from localStorage directly.
 */
export function backendGet(key: string): string | null {
  if (native) {
    return cache.get(key) ?? null;
  }

  // Web fallback
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Write a value to the storage backend.
 * Returns true synchronously. On native, the async Preferences.set() fires in the background.
 */
export function backendSet(key: string, value: string): boolean {
  if (native) {
    cache.set(key, value);
    // Fire-and-forget async write to native storage
    Preferences.set({ key, value }).catch((err) => {
      console.warn(`[StorageBackend] Failed to persist key "${key}" to Preferences:`, err);
    });
    // Also dual-write to localStorage for web fallback
    try {
      localStorage.setItem(key, value);
    } catch {
      // Quota exceeded on web side is non-critical since native is primary
    }
    return true;
  }

  // Web fallback — let quota errors propagate so storageService can handle them
  if (typeof window === 'undefined') return false;
  localStorage.setItem(key, value);
  return true;
}

/**
 * Remove a value from the storage backend.
 */
export function backendRemove(key: string): void {
  if (native) {
    cache.delete(key);
    Preferences.remove({ key }).catch(() => {});
    try {
      localStorage.removeItem(key);
    } catch {
      // Non-critical
    }
    return;
  }

  // Web fallback
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(key);
  } catch {
    // Non-critical
  }
}

/**
 * Get all keys in the storage backend.
 */
export function backendKeys(): string[] {
  if (native) {
    return Array.from(cache.keys());
  }

  // Web fallback
  if (typeof window === 'undefined') return [];
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) keys.push(key);
  }
  return keys;
}
